import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { User, Save, Eye, EyeOff, Loader2 } from "lucide-react";

export default function Profile() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/", { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/profile/me").then((r) => setProfile({ ...r.data, links: r.data.links || {} }));
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile/me", {
        headline: profile.headline || "", bio: profile.bio || "",
        skills: profile.skills || [], experience_years: Number(profile.experience_years) || 0,
        location: profile.location || "", links: profile.links || {}, public: !!profile.public,
      });
      toast.success("Profile saved");
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  const addSkill = () => {
    const s = skillInput.trim();
    if (!s) return;
    if ((profile.skills || []).includes(s)) return;
    setProfile((p) => ({ ...p, skills: [...(p.skills || []), s] }));
    setSkillInput("");
  };

  if (!profile) return <div className="max-w-4xl mx-auto p-12 text-zinc-500 font-mono-alt uppercase text-sm">Loading…</div>;

  const publicUrl = `${window.location.origin}/u/${user.user_id}`;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2">Skill Bank</div>
      <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">
        Your <span className="text-yellow-500">profile</span>
      </h1>
      <p className="text-zinc-400 mt-4 max-w-2xl">Build a public profile once. Employers can find you, and you can one-click apply to any listing.</p>

      <div className="mt-6 flex items-center gap-3 border border-zinc-800 bg-zinc-950 p-4">
        {profile.public ? <Eye size={16} className="text-yellow-500" /> : <EyeOff size={16} className="text-zinc-500" />}
        <div className="flex-1">
          <div className="text-sm text-white">Public profile: <span className="text-yellow-500 font-bold">{profile.public ? "ON" : "OFF"}</span></div>
          {profile.public && <div className="text-xs text-zinc-500 font-mono-alt break-all">{publicUrl}</div>}
        </div>
        <button
          data-testid="profile-toggle-public"
          type="button"
          onClick={() => setProfile((p) => ({ ...p, public: !p.public }))}
          className="border border-zinc-700 hover:border-yellow-500 text-zinc-300 hover:text-yellow-500 uppercase text-xs tracking-wider px-4 py-2"
        >Toggle</button>
      </div>

      <form onSubmit={save} className="mt-6 space-y-4">
        <Field label="Headline">
          <input data-testid="profile-headline" value={profile.headline || ""} onChange={(e) => setProfile((p) => ({ ...p, headline: e.target.value }))} placeholder="Electrical apprentice, comfortable at heights" className={inputCls} />
        </Field>
        <Field label="Bio">
          <textarea data-testid="profile-bio" rows={4} value={profile.bio || ""} onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))} placeholder="Tell employers about your experience, interests, and goals." className={`${inputCls} resize-none`} />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Location">
            <input data-testid="profile-location" value={profile.location || ""} onChange={(e) => setProfile((p) => ({ ...p, location: e.target.value }))} placeholder="Austin, TX" className={inputCls} />
          </Field>
          <Field label="Years of experience">
            <input data-testid="profile-experience" type="number" min="0" max="60" value={profile.experience_years || 0} onChange={(e) => setProfile((p) => ({ ...p, experience_years: e.target.value }))} className={inputCls} />
          </Field>
        </div>
        <Field label="Skills">
          <div className="flex gap-2">
            <input data-testid="profile-skill-input" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} placeholder="e.g. Welding" className={inputCls} />
            <button data-testid="profile-skill-add" type="button" onClick={addSkill} className="bg-yellow-500 text-black font-bold uppercase tracking-wider px-4 hover:bg-yellow-400">Add</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {(profile.skills || []).map((s) => (
              <span key={s} className="flex items-center gap-1 text-xs font-mono-alt uppercase tracking-widest border border-zinc-800 px-3 py-1 text-zinc-200">
                {s}
                <button type="button" onClick={() => setProfile((p) => ({ ...p, skills: p.skills.filter((x) => x !== s) }))} className="text-zinc-500 hover:text-yellow-500 ml-2">×</button>
              </span>
            ))}
          </div>
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="LinkedIn URL">
            <input data-testid="profile-linkedin" value={profile.links?.linkedin || ""} onChange={(e) => setProfile((p) => ({ ...p, links: { ...p.links, linkedin: e.target.value } }))} placeholder="https://linkedin.com/in/..." className={inputCls} />
          </Field>
          <Field label="Portfolio / Website">
            <input data-testid="profile-website" value={profile.links?.website || ""} onChange={(e) => setProfile((p) => ({ ...p, links: { ...p.links, website: e.target.value } }))} placeholder="https://your-site.com" className={inputCls} />
          </Field>
        </div>

        <button
          data-testid="profile-save"
          disabled={saving}
          className="bg-yellow-500 text-black font-bold uppercase tracking-wider px-8 py-3 hover:bg-yellow-400 transition disabled:opacity-60 flex items-center gap-2"
        >
          {saving ? <><Loader2 className="animate-spin" size={16} /> Saving</> : <><Save size={16} /> Save Profile</>}
        </button>
      </form>
    </div>
  );
}

const inputCls = "w-full bg-zinc-950 border border-zinc-800 focus:border-yellow-500 px-3 py-2.5 text-white placeholder:text-zinc-600 focus:outline-none transition";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">{label}</span>
      {children}
    </label>
  );
}
